import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Globe2 as OrbitIcon, Users, Calendar, Target, Plus, Check, 
  Share2, Loader2, AlertTriangle, ChevronDown, ChevronUp, Zap, 
  TrendingUp, MessageSquare, Flag, Copy, X
} from 'lucide-react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../utils/api';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';

const STATUS_OPTIONS = ['Planning', 'Started', 'Building', 'Stuck', 'Almost Done', 'Completed'];
const STATUS_COLORS = {
  Planning: '#A1A1AA',
  Started: '#4A90E2',
  Building: '#D4AF37',
  Stuck: '#E24A4A',
  'Almost Done': '#50C878',
  Completed: '#17BEBB',
};

const ROLE_OPTIONS = ['Leader', 'Core Team', 'Contributor', 'Advisor'];

const OrbitDetail = () => {
  const { orbitId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [orbit, setOrbit] = useState(null);
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [showAddMilestone, setShowAddMilestone] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [checkInData, setCheckInData] = useState({
    completed_this_week: '',
    working_on_next: '',
    is_blocked: false,
    blocker_explanation: '',
    energy_level: 5,
    motivation_level: 5,
    confidence_level: 5
  });
  const [newMilestone, setNewMilestone] = useState({ title: '', description: '', target_date: '' });
  const [submitting, setSubmitting] = useState(false);

  const t = {
    en: {
      orbitScore: 'Orbit Score',
      members: 'Members',
      milestones: 'Milestones',
      activity: 'Activity',
      checkIn: 'Weekly Check-In',
      addMilestone: 'Add Milestone',
      shareOrbit: 'Share Orbit',
      copyLink: 'Copy Invite Link',
      linkCopied: 'Invite link copied!',
      status: 'Status',
      mission: 'Mission',
      successLooks: 'Success Looks Like',
      biggestChallenge: 'Biggest Challenge',
      targetDate: 'Target Date',
      noMilestones: 'No milestones yet',
      noActivity: 'No activity yet',
      submitCheckIn: 'Submit Check-In',
      completedThisWeek: 'What did you complete this week?',
      workingOnNext: 'What are you working on next?',
      areYouBlocked: 'Are you blocked?',
      blockerExplanation: 'What is blocking you?',
      energyLevel: 'Energy Level',
      motivationLevel: 'Motivation Level',
      confidenceLevel: 'Confidence project will succeed',
      milestoneTitle: 'Milestone Title',
      milestoneDesc: 'Description (optional)',
      milestoneDate: 'Target Date (optional)',
      owner: 'Owner',
      inviteCode: 'Invite Code',
    },
    tr: {
      orbitScore: 'Orbit Skoru',
      members: 'Üyeler',
      milestones: 'Kilometre Taşları',
      activity: 'Aktivite',
      checkIn: 'Haftalık Check-In',
      addMilestone: 'Kilometre Taşı Ekle',
      shareOrbit: 'Orbit\'i Paylaş',
      copyLink: 'Davet Linkini Kopyala',
      linkCopied: 'Davet linki kopyalandı!',
      status: 'Durum',
      mission: 'Misyon',
      successLooks: 'Başarı Neye Benziyor',
      biggestChallenge: 'En Büyük Zorluk',
      targetDate: 'Hedef Tarih',
      noMilestones: 'Henüz kilometre taşı yok',
      noActivity: 'Henüz aktivite yok',
      submitCheckIn: 'Check-In Gönder',
      completedThisWeek: 'Bu hafta ne tamamladınız?',
      workingOnNext: 'Sırada ne var?',
      areYouBlocked: 'Takıldınız mı?',
      blockerExplanation: 'Sizi ne engelliyor?',
      energyLevel: 'Enerji Seviyesi',
      motivationLevel: 'Motivasyon Seviyesi',
      confidenceLevel: 'Projenin başarılı olacağına güven',
      milestoneTitle: 'Kilometre Taşı Başlığı',
      milestoneDesc: 'Açıklama (opsiyonel)',
      milestoneDate: 'Hedef Tarih (opsiyonel)',
      owner: 'Sahip',
      inviteCode: 'Davet Kodu',
    }
  };

  const c = t[language] || t.en;

  useEffect(() => {
    fetchOrbit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orbitId]);

  const fetchOrbit = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/orbit/${orbitId}`);
      setOrbit(response.data);
    } catch (error) {
      console.error('Error fetching orbit:', error);
      if (error.response?.status === 404) {
        toast.error(language === 'tr' ? 'Orbit bulunamadı' : 'Orbit not found');
        navigate('/orbit');
      } else if (error.response?.status === 403) {
        toast.error(language === 'tr' ? 'Bu Orbit\'e erişiminiz yok' : 'You do not have access to this Orbit');
        navigate('/orbit');
      }
    } finally {
      setLoading(false);
    }
  };

  const copyInviteLink = () => {
    const inviteUrl = `${window.location.origin}/orbit/join/${orbit.invite_code}`;
    navigator.clipboard.writeText(inviteUrl);
    toast.success(c.linkCopied);
  };

  const handleCheckInSubmit = async () => {
    if (!checkInData.completed_this_week || !checkInData.working_on_next) {
      toast.error(language === 'tr' ? 'Lütfen gerekli alanları doldurun' : 'Please fill required fields');
      return;
    }

    try {
      setSubmitting(true);
      await api.post(`/orbit/${orbitId}/checkin`, checkInData);
      toast.success(language === 'tr' ? 'Check-in gönderildi!' : 'Check-in submitted!');
      setShowCheckIn(false);
      setCheckInData({
        completed_this_week: '',
        working_on_next: '',
        is_blocked: false,
        blocker_explanation: '',
        energy_level: 5,
        motivation_level: 5,
        confidence_level: 5
      });
      fetchOrbit();
    } catch (error) {
      toast.error(language === 'tr' ? 'Check-in gönderilemedi' : 'Failed to submit check-in');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddMilestone = async () => {
    if (!newMilestone.title) {
      toast.error(language === 'tr' ? 'Başlık gerekli' : 'Title is required');
      return;
    }

    try {
      setSubmitting(true);
      await api.post(`/orbit/${orbitId}/milestone`, newMilestone);
      toast.success(language === 'tr' ? 'Kilometre taşı eklendi!' : 'Milestone added!');
      setShowAddMilestone(false);
      setNewMilestone({ title: '', description: '', target_date: '' });
      fetchOrbit();
    } catch (error) {
      toast.error(language === 'tr' ? 'Kilometre taşı eklenemedi' : 'Failed to add milestone');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleMilestoneComplete = async (milestoneId) => {
    try {
      await api.put(`/orbit/${orbitId}/milestone/${milestoneId}/complete`);
      fetchOrbit();
    } catch (error) {
      toast.error(language === 'tr' ? 'Güncellenemedi' : 'Failed to update');
    }
  };

  const updateStatus = async (newStatus) => {
    try {
      await api.put(`/orbit/${orbitId}`, { current_status: newStatus });
      setOrbit(prev => ({ ...prev, current_status: newStatus }));
      toast.success(language === 'tr' ? 'Durum güncellendi' : 'Status updated');
    } catch (error) {
      toast.error(language === 'tr' ? 'Durum güncellenemedi' : 'Failed to update status');
    }
  };

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ position: 'relative', zIndex: 1 }}>
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (!orbit) return null;

  // Orbit parameter keys for navigation
  const ORBIT_PARAMS = [
    { key: 'vision', en: 'Vision', tr: 'Vizyon' },
    { key: 'planning', en: 'Planning', tr: 'Planlama' },
    { key: 'execution', en: 'Execution', tr: 'Uygulama' },
    { key: 'team_energy', en: 'Team Energy', tr: 'Takım Enerjisi' },
    { key: 'resources', en: 'Resources', tr: 'Kaynaklar' },
    { key: 'communication', en: 'Communication', tr: 'İletişim' },
    { key: 'momentum', en: 'Momentum', tr: 'Momentum' },
    { key: 'obstacles', en: 'Obstacles', tr: 'Engeller' },
    { key: 'leadership', en: 'Leadership', tr: 'Liderlik' },
    { key: 'creativity', en: 'Creativity', tr: 'Yaratıcılık' },
    { key: 'discipline', en: 'Discipline', tr: 'Disiplin' },
    { key: 'progress', en: 'Progress', tr: 'İlerleme' },
  ];

  // Prepare radar chart data
  const orbitScores = orbit.orbit_scores || {};
  const radarData = [
    { subject: language === 'tr' ? 'Vizyon' : 'Vision', key: 'vision', value: orbitScores.vision || 5, fullMark: 10 },
    { subject: language === 'tr' ? 'Planlama' : 'Planning', key: 'planning', value: orbitScores.planning || 5, fullMark: 10 },
    { subject: language === 'tr' ? 'Uygulama' : 'Execution', key: 'execution', value: orbitScores.execution || 5, fullMark: 10 },
    { subject: language === 'tr' ? 'Takım Enerjisi' : 'Team Energy', key: 'team_energy', value: orbitScores.team_energy || 5, fullMark: 10 },
    { subject: language === 'tr' ? 'Kaynaklar' : 'Resources', key: 'resources', value: orbitScores.resources || 5, fullMark: 10 },
    { subject: language === 'tr' ? 'İletişim' : 'Communication', key: 'communication', value: orbitScores.communication || 5, fullMark: 10 },
    { subject: language === 'tr' ? 'Momentum' : 'Momentum', key: 'momentum', value: orbitScores.momentum || 5, fullMark: 10 },
    { subject: language === 'tr' ? 'Engeller' : 'Obstacles', key: 'obstacles', value: orbitScores.obstacles || 5, fullMark: 10 },
    { subject: language === 'tr' ? 'Liderlik' : 'Leadership', key: 'leadership', value: orbitScores.leadership || 5, fullMark: 10 },
    { subject: language === 'tr' ? 'Yaratıcılık' : 'Creativity', key: 'creativity', value: orbitScores.creativity || 5, fullMark: 10 },
    { subject: language === 'tr' ? 'Disiplin' : 'Discipline', key: 'discipline', value: orbitScores.discipline || 5, fullMark: 10 },
    { subject: language === 'tr' ? 'İlerleme' : 'Progress', key: 'progress', value: orbitScores.completion_progress || 0, fullMark: 10 },
  ];

  // Custom tick component for clickable labels
  const OrbitParamTick = ({ payload, x, y, textAnchor, index }) => {
    const paramKey = radarData[index]?.key;
    return (
      <g 
        transform={`translate(${x},${y})`} 
        onClick={() => navigate(`/orbit/${orbitId}/mission-control/${paramKey}`)}
        style={{ cursor: 'pointer' }}
        data-testid={`orbit-param-${paramKey}`}
      >
        <text
          fill="#D4AF37"
          fontSize={9}
          fontFamily="Outfit"
          textAnchor={textAnchor}
          className="hover:fill-white transition-colors"
        >
          {payload.value}
        </text>
      </g>
    );
  };

  const completedMilestones = orbit.milestones?.filter(m => m.completed).length || 0;
  const totalMilestones = orbit.milestones?.length || 0;

  return (
    <div
      className="min-h-screen pb-36 lg:pb-24 px-4"
      style={{ position: 'relative', zIndex: 1, paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1.5rem)' }}
    >
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-6"
        >
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/orbit')}
              className="text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <OrbitIcon className="w-6 h-6 text-[#D4AF37]" />
                <h1 className="font-serif text-xl text-[#F8F5F2]">{orbit.project_name}</h1>
              </div>
              <p className="text-[#A1A1AA] text-sm">{orbit.project_type}</p>
            </div>
          </div>
          
          <button
            onClick={copyInviteLink}
            className="p-2 rounded-lg bg-[#D4AF37]/20 text-[#D4AF37] hover:bg-[#D4AF37]/30 transition-colors"
            title={c.copyLink}
          >
            <Share2 className="w-5 h-5" />
          </button>
        </motion.div>

        {/* Orbit Score & Map */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-panel rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-sans text-sm text-[#A1A1AA] uppercase tracking-wide">{c.orbitScore}</h2>
            <div className="text-3xl font-bold text-[#D4AF37]">{orbit.orbit_score}</div>
          </div>
          
          {/* Radar Chart */}
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData}>
                <PolarGrid stroke="#ffffff20" />
                <PolarAngleAxis 
                  dataKey="subject" 
                  tick={<OrbitParamTick />}
                />
                <PolarRadiusAxis 
                  domain={[0, 10]} 
                  tick={{ fill: '#6B7280', fontSize: 8 }}
                  axisLine={false}
                />
                <Radar
                  name="Orbit"
                  dataKey="value"
                  stroke="#D4AF37"
                  fill="#D4AF37"
                  fillOpacity={0.3}
                  strokeWidth={2}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <p className="text-center text-xs text-[#6B7280] mt-2">
            {language === 'tr' ? 'Detaylar için parametre isimlerine tıklayın' : 'Click parameter names for details'}
          </p>
        </motion.div>

        {/* Status & Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 gap-3 mb-6"
        >
          {/* Status Dropdown */}
          <div className="glass-panel rounded-xl p-4">
            <p className="text-xs text-[#A1A1AA] mb-2">{c.status}</p>
            <select
              value={orbit.current_status}
              onChange={(e) => updateStatus(e.target.value)}
              disabled={!orbit.is_owner}
              className="w-full bg-transparent text-[#F8F5F2] font-medium focus:outline-none cursor-pointer disabled:cursor-default"
              style={{ color: STATUS_COLORS[orbit.current_status] }}
            >
              {STATUS_OPTIONS.map(status => (
                <option key={status} value={status} className="bg-[#0a0a0f]">
                  {status}
                </option>
              ))}
            </select>
          </div>

          {/* Members Count */}
          <button
            onClick={() => setShowMembers(!showMembers)}
            className="glass-panel rounded-xl p-4 text-left hover:border-[#D4AF37]/30 transition-colors"
          >
            <p className="text-xs text-[#A1A1AA] mb-1">{c.members}</p>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-[#F8F5F2] font-medium">{orbit.members?.length || 0}</span>
              {showMembers ? <ChevronUp className="w-4 h-4 text-[#A1A1AA] ml-auto" /> : <ChevronDown className="w-4 h-4 text-[#A1A1AA] ml-auto" />}
            </div>
          </button>
        </motion.div>

        {/* Members List (Expandable) */}
        <AnimatePresence>
          {showMembers && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="glass-panel rounded-xl p-4 mb-6 overflow-hidden"
            >
              <div className="space-y-3">
                {orbit.members?.map((member, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div>
                      <p className="text-[#F8F5F2] text-sm">{member.name}</p>
                      <p className="text-[#A1A1AA] text-xs">{member.role}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1 text-xs">
                        <Zap className="w-3 h-3 text-yellow-400" />
                        <span className="text-[#A1A1AA]">{member.energy_level}</span>
                      </div>
                      {member.responsibility_level === 'Leader' && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-[#D4AF37]/20 text-[#D4AF37]">
                          {c.owner}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Invite Code */}
              <div className="mt-4 pt-4 border-t border-white/10">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#A1A1AA]">{c.inviteCode}</span>
                  <button
                    onClick={copyInviteLink}
                    className="flex items-center gap-2 text-[#D4AF37] text-sm hover:text-[#D4AF37]/80"
                  >
                    <code className="bg-white/5 px-2 py-1 rounded">{orbit.invite_code}</code>
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Action Buttons */}
        <div className="flex gap-3 mb-6">
          <button
            onClick={() => setShowCheckIn(true)}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#D4AF37] text-[#030305] font-semibold hover:bg-[#D4AF37]/90 transition-all"
          >
            <MessageSquare className="w-5 h-5" />
            {c.checkIn}
          </button>
          <button
            onClick={() => setShowAddMilestone(true)}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border border-[#D4AF37]/30 text-[#D4AF37] font-semibold hover:bg-[#D4AF37]/10 transition-all"
          >
            <Flag className="w-5 h-5" />
            {c.addMilestone}
          </button>
        </div>

        {/* Mission & Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="glass-panel rounded-2xl p-5 mb-6 space-y-4"
        >
          <div>
            <p className="text-xs text-[#A1A1AA] mb-1">{c.mission}</p>
            <p className="text-[#F8F5F2] text-sm">{orbit.project_mission}</p>
          </div>
          <div>
            <p className="text-xs text-[#A1A1AA] mb-1">{c.successLooks}</p>
            <p className="text-[#F8F5F2] text-sm">{orbit.success_outcome}</p>
          </div>
          {orbit.biggest_challenge && (
            <div>
              <p className="text-xs text-[#A1A1AA] mb-1">{c.biggestChallenge}</p>
              <p className="text-[#F8F5F2] text-sm">{orbit.biggest_challenge}</p>
            </div>
          )}
          {orbit.target_completion_date && (
            <div className="flex items-center gap-2 text-[#A1A1AA] text-sm">
              <Calendar className="w-4 h-4" />
              <span>{c.targetDate}: {new Date(orbit.target_completion_date).toLocaleDateString()}</span>
            </div>
          )}
        </motion.div>

        {/* Milestones */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-panel rounded-2xl p-5 mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Flag className="w-5 h-5 text-[#D4AF37]" />
              <h3 className="font-sans text-[#F8F5F2]">{c.milestones}</h3>
            </div>
            <span className="text-sm text-[#A1A1AA]">{completedMilestones}/{totalMilestones}</span>
          </div>
          
          {orbit.milestones?.length > 0 ? (
            <div className="space-y-3">
              {orbit.milestones.map((milestone) => (
                <div
                  key={milestone.id}
                  className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${
                    milestone.completed ? 'bg-[#50C878]/10' : 'bg-white/5'
                  }`}
                >
                  <button
                    onClick={() => toggleMilestoneComplete(milestone.id)}
                    className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                      milestone.completed
                        ? 'bg-[#50C878] border-[#50C878]'
                        : 'border-[#A1A1AA] hover:border-[#D4AF37]'
                    }`}
                  >
                    {milestone.completed && <Check className="w-3 h-3 text-white" />}
                  </button>
                  <div className="flex-1">
                    <p className={`text-sm ${milestone.completed ? 'text-[#A1A1AA] line-through' : 'text-[#F8F5F2]'}`}>
                      {milestone.title}
                    </p>
                    {milestone.description && (
                      <p className="text-xs text-[#6B7280] mt-1">{milestone.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#A1A1AA] text-sm text-center py-4">{c.noMilestones}</p>
          )}
        </motion.div>

        {/* Activity Feed */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="glass-panel rounded-2xl p-5"
        >
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
            <h3 className="font-sans text-[#F8F5F2]">{c.activity}</h3>
          </div>
          
          {orbit.activity_feed?.length > 0 ? (
            <div className="space-y-3">
              {orbit.activity_feed.map((activity, idx) => (
                <div key={idx} className="flex items-start gap-3 text-sm">
                  <div className="w-2 h-2 rounded-full bg-[#D4AF37] mt-2" />
                  <div>
                    <p className="text-[#F8F5F2]">
                      <span className="text-[#D4AF37]">{activity.user_name}</span>
                      {' '}{activity.summary}
                    </p>
                    <p className="text-xs text-[#6B7280]">
                      {new Date(activity.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#A1A1AA] text-sm text-center py-4">{c.noActivity}</p>
          )}
        </motion.div>
      </div>

      {/* Check-In Modal */}
      <AnimatePresence>
        {showCheckIn && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center"
            onClick={() => setShowCheckIn(false)}
          >
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-[#0a0a0f] rounded-t-3xl sm:rounded-2xl p-6 max-h-[85vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-xl text-[#F8F5F2]">{c.checkIn}</h2>
                <button onClick={() => setShowCheckIn(false)} className="text-[#A1A1AA] hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-5">
                <div>
                  <label className="text-sm text-[#A1A1AA] mb-2 block">{c.completedThisWeek} *</label>
                  <textarea
                    value={checkInData.completed_this_week}
                    onChange={(e) => setCheckInData(prev => ({ ...prev, completed_this_week: e.target.value }))}
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-[#F8F5F2] placeholder-[#6B7280] focus:border-[#D4AF37]/50 focus:outline-none resize-none"
                  />
                </div>
                
                <div>
                  <label className="text-sm text-[#A1A1AA] mb-2 block">{c.workingOnNext} *</label>
                  <textarea
                    value={checkInData.working_on_next}
                    onChange={(e) => setCheckInData(prev => ({ ...prev, working_on_next: e.target.value }))}
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-[#F8F5F2] placeholder-[#6B7280] focus:border-[#D4AF37]/50 focus:outline-none resize-none"
                  />
                </div>
                
                <div>
                  <label className="flex items-center gap-2 text-sm text-[#A1A1AA] mb-2">
                    <input
                      type="checkbox"
                      checked={checkInData.is_blocked}
                      onChange={(e) => setCheckInData(prev => ({ ...prev, is_blocked: e.target.checked }))}
                      className="rounded"
                    />
                    {c.areYouBlocked}
                  </label>
                  {checkInData.is_blocked && (
                    <textarea
                      value={checkInData.blocker_explanation}
                      onChange={(e) => setCheckInData(prev => ({ ...prev, blocker_explanation: e.target.value }))}
                      placeholder={c.blockerExplanation}
                      rows={2}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-[#F8F5F2] placeholder-[#6B7280] focus:border-[#D4AF37]/50 focus:outline-none resize-none"
                    />
                  )}
                </div>
                
                {/* Sliders */}
                {[
                  { key: 'energy_level', label: c.energyLevel },
                  { key: 'motivation_level', label: c.motivationLevel },
                  { key: 'confidence_level', label: c.confidenceLevel },
                ].map(({ key, label }) => (
                  <div key={key}>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-[#A1A1AA]">{label}</span>
                      <span className="text-[#D4AF37] font-medium">{checkInData[key]}/10</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={checkInData[key]}
                      onChange={(e) => setCheckInData(prev => ({ ...prev, [key]: parseInt(e.target.value) }))}
                      className="w-full accent-[#D4AF37]"
                    />
                  </div>
                ))}
                
                <button
                  onClick={handleCheckInSubmit}
                  disabled={submitting}
                  className="w-full py-3.5 rounded-xl bg-[#D4AF37] text-[#030305] font-semibold hover:bg-[#D4AF37]/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                  {c.submitCheckIn}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Milestone Modal */}
      <AnimatePresence>
        {showAddMilestone && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center"
            onClick={() => setShowAddMilestone(false)}
          >
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-[#0a0a0f] rounded-t-3xl sm:rounded-2xl p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-xl text-[#F8F5F2]">{c.addMilestone}</h2>
                <button onClick={() => setShowAddMilestone(false)} className="text-[#A1A1AA] hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-[#A1A1AA] mb-2 block">{c.milestoneTitle} *</label>
                  <input
                    type="text"
                    value={newMilestone.title}
                    onChange={(e) => setNewMilestone(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-[#F8F5F2] focus:border-[#D4AF37]/50 focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="text-sm text-[#A1A1AA] mb-2 block">{c.milestoneDesc}</label>
                  <textarea
                    value={newMilestone.description}
                    onChange={(e) => setNewMilestone(prev => ({ ...prev, description: e.target.value }))}
                    rows={2}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-[#F8F5F2] focus:border-[#D4AF37]/50 focus:outline-none resize-none"
                  />
                </div>
                
                <div>
                  <label className="text-sm text-[#A1A1AA] mb-2 block">{c.milestoneDate}</label>
                  <input
                    type="date"
                    value={newMilestone.target_date}
                    onChange={(e) => setNewMilestone(prev => ({ ...prev, target_date: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-[#F8F5F2] focus:border-[#D4AF37]/50 focus:outline-none"
                  />
                </div>
                
                <button
                  onClick={handleAddMilestone}
                  disabled={submitting}
                  className="w-full py-3.5 rounded-xl bg-[#D4AF37] text-[#030305] font-semibold hover:bg-[#D4AF37]/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
                  {c.addMilestone}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <BottomNav />
    </div>
  );
};

export default OrbitDetail;
